import React from 'react';
import { Flex, Layout, Typography } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useBrandAssets } from '../../../../theme/useBrandAssets';
import { LanguageSwitcher } from '../../../language';
import { NS } from '../../i18n';
import loaderSrc from '../../../../assets/icons/loader.svg';
import './AuthLayout.css';

const { Title } = Typography;
const { Footer } = Layout;

interface AuthLayoutProps {
  /** The form card on the right-hand side. */
  children: React.ReactNode;
  /** Optional link above the layout, e.g. "Back to log in" on the recover screen. */
  backLink?: { label: string; onClick: () => void };
}

/**
 * The shell every signed-out screen shares: brand panel (logo + welcome), the
 * form slot, the footer, and the language picker — the user has no session
 * yet, so MainLayout (where the picker otherwise lives) isn't mounted.
 *
 * Login and Recover account used to copy this markup and ship two near-identical
 * global stylesheets; the login's inputs and button were only styled when the
 * recover screen's sheet happened to be loaded. One layout, one stylesheet.
 * The loading skeleton (AuthPageSkeleton) renders this same layout, so the page
 * doesn't jump when the real form replaces it.
 */
export function AuthLayout({ children, backLink }: AuthLayoutProps): React.ReactElement {
  const { t } = useTranslation(NS);
  const { assets, meta } = useBrandAssets();

  return (
    <div className="sign-in-outer-container">
      <LanguageSwitcher />

      {backLink && (
        <div className="auth-back-link">
          <Typography.Link
            onClick={backLink.onClick}
            className="auth-back-link__content"
            // Same id as the old RecoverAccount had, kept stable for any selector using it.
            id="back-to-login"
          >
            <LeftOutlined /> {backLink.label}
          </Typography.Link>
        </div>
      )}

      <Flex className="sign-in-layout" justify="space-around" align="center">
        <div className="sign-in-left-section">
          <div className="sign-in-brand-section">
            <img
              src={assets.logoOnBackground}
              alt={meta.title}
              className="sign-in-logo-img"
              fetchPriority="high"
              decoding="async"
              width="433"
              height="135"
            />
            <img src={loaderSrc} alt="" aria-hidden className="sign-in-loader" />
            <Title level={1} className="sign-in-welcome-text">
              {t('layout.welcomeLine1')} <br /> {t('layout.welcomeLine2')}
            </Title>
          </div>
        </div>

        <main className="sign-in-right-section">{children}</main>
      </Flex>

      <Footer className="sign-in-footer">
        {/* The brand's tagline, kept in English in every language — like the logo. */}
        {meta.tagline && <span>{meta.tagline}</span>}
        <span>{meta.website}</span>
      </Footer>
    </div>
  );
}
